import { Avatar, Badge, type BadgeVariant, type DataTableColumn, Select } from "@design";
import { useTranslation } from "react-i18next";
import type { UserResponse } from "@/api";
import { Text } from "@/components/Text";
import { formatRelativeTime } from "@/i18n/format";
import { initialsOf } from "@/lib/initials";
import { assignableRoles, canManageUser, type UserRole, type UserStatus, userStatus } from "./userRules";

const STATUS_VARIANT: Record<UserStatus, BadgeVariant> = {
  active: "success",
  pending: "warning",
  deactivated: "neutral",
};

interface Me {
  uuid: string;
  role: string;
}

function UserCell({ user, isMe }: Readonly<{ user: UserResponse; isMe: boolean }>) {
  const { t } = useTranslation("admin");
  return (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar initials={initialsOf(user.name || user.email)} src={user.avatarUrl ?? undefined} size="sm" />
      <div className="flex min-w-0 flex-col text-left">
        <span className="flex items-center gap-2">
          <Text as="span" variant="body-sm" className="truncate">
            {user.name || user.email}
          </Text>
          {isMe && (
            <Badge size="compact" variant="neutral">
              {t("users.you")}
            </Badge>
          )}
        </span>
        <Text as="span" variant="caption" tone="tertiary" className="truncate">
          {user.email}
        </Text>
      </div>
    </div>
  );
}

function RoleCell({
  user,
  me,
  canEdit,
  onChange,
}: Readonly<{ user: UserResponse; me: Me; canEdit: boolean; onChange: (role: string) => void }>) {
  const { t } = useTranslation("admin");
  const label = t(`users.roles.${user.role as UserRole}`, { defaultValue: user.role });
  if (!canEdit || !user.isActive || !canManageUser(user, me)) {
    return <Badge variant={user.role === "member" ? "neutral" : "info"}>{label}</Badge>;
  }
  return (
    <div className="w-44 md:ml-0 ml-auto">
      <Select
        size="sm"
        aria-label={t("users.roleFor", { name: user.name || user.email })}
        value={user.role}
        options={assignableRoles(me.role).map((role) => ({ value: role, label: t(`users.roles.${role}`) }))}
        onChange={(role) => role !== user.role && onChange(role)}
      />
    </div>
  );
}

function StatusCell({ user }: Readonly<{ user: UserResponse }>) {
  const { t } = useTranslation("admin");
  const status = userStatus(user);
  return (
    <div className="flex flex-col items-end gap-1 md:items-start">
      <Badge variant={STATUS_VARIANT[status]}>{t(`users.status.${status}`)}</Badge>
      {status === "deactivated" && user.deactivationReason && (
        <Text as="span" variant="caption" tone="tertiary" className="max-w-56 truncate">
          {user.deactivationReason}
        </Text>
      )}
    </div>
  );
}

export function useUserColumns({
  me,
  canEdit,
  onRoleChange,
}: Readonly<{ me: Me; canEdit: boolean; onRoleChange: (user: UserResponse, role: string) => void }>) {
  const { t } = useTranslation("admin");
  const columns: DataTableColumn<UserResponse>[] = [
    {
      key: "user",
      header: t("users.columns.user"),
      mobile: "primary",
      render: (user) => <UserCell user={user} isMe={user.uuid === me.uuid} />,
    },
    {
      key: "role",
      header: t("users.columns.role"),
      interactive: true,
      render: (user) => (
        <RoleCell user={user} me={me} canEdit={canEdit} onChange={(role) => onRoleChange(user, role)} />
      ),
    },
    { key: "status", header: t("users.columns.status"), render: (user) => <StatusCell user={user} /> },
    {
      key: "lastLogin",
      header: t("users.columns.lastLogin"),
      render: (user) => (
        <Text as="span" variant="body-sm" tone="tertiary">
          {user.lastLoginAt ? formatRelativeTime(user.lastLoginAt) : t("users.never")}
        </Text>
      ),
    },
  ];
  return columns;
}
